import React from 'react';

import { Button, Card, Checkbox, Datepicker, PillSwitch, PillSwitchOption, Select } from '@/ui';
import { dateFromDateStringWithAppendedTime } from '@/utils/dates';

import {
  TIMELINE_EVENT_GROUPS,
  TIMELINE_EVENT_TYPES,
  TIMELINE_PRESETS,
  eventTypesInGroup,
  matchingPreset,
  presetEventTypes
} from '../eventTypes';

const EARLIEST_SELECTABLE_DATE = new Date(2000, 0, 1);

interface TimelineControlsProps {
  dateRange: { start?: string; end?: string };
  onDateRangeChange: (range: { start?: string; end?: string }) => void;
  policyOptions: Array<{ value: string; label: string }>;
  selectedPolicyId: string | null;
  onPolicyChange: (policyId: string | null) => void;
  sortOrder: 'asc' | 'desc';
  onSortOrderChange: (order: 'asc' | 'desc') => void;
  selectedEventTypes: Set<string>;
  onEventTypesChange: (types: Set<string>) => void;
  eventCounts?: { shown: number; total: number };
  hasActiveFilters: boolean;
  onResetFilters: () => void;
}

const TimelineControls: React.FC<TimelineControlsProps> = ({
  dateRange,
  onDateRangeChange,
  policyOptions,
  selectedPolicyId,
  onPolicyChange,
  sortOrder,
  onSortOrderChange,
  selectedEventTypes,
  onEventTypesChange,
  eventCounts,
  hasActiveFilters,
  onResetFilters
}) => {
  const toggleEventType = (type: string) => {
    const newTypes = new Set(selectedEventTypes);
    if (newTypes.has(type)) {
      newTypes.delete(type);
    } else {
      newTypes.add(type);
    }
    onEventTypesChange(newTypes);
  };

  const setGroupSelected = (types: string[], isSelected: boolean) => {
    const newTypes = new Set(selectedEventTypes);
    types.forEach((type) => (isSelected ? newTypes.add(type) : newTypes.delete(type)));
    onEventTypesChange(newTypes);
  };

  return (
    <Card className="mb-4">
      <div className="flex flex-wrap items-start gap-4">
        <div className="w-44">
          <Datepicker
            isClearable={false}
            label="From"
            minDate={EARLIEST_SELECTABLE_DATE}
            name="timeline-start"
            onChange={(start) => onDateRangeChange({ ...dateRange, start: start ?? undefined })}
            value={dateRange.start}
          />
        </div>
        <div className="w-44">
          <Datepicker
            isClearable={false}
            label="To"
            minDate={
              dateRange.start
                ? dateFromDateStringWithAppendedTime(dateRange.start)
                : EARLIEST_SELECTABLE_DATE
            }
            name="timeline-end"
            onChange={(end) => onDateRangeChange({ ...dateRange, end: end ?? undefined })}
            value={dateRange.end}
          />
        </div>
        <Select
          className="min-w-64 flex-1"
          debounce={false}
          label="Policy"
          onChange={(option) => onPolicyChange(option?.value || null)}
          options={policyOptions}
          placeholder="All policies"
          selectedOption={policyOptions.find((option) => option.value === selectedPolicyId) ?? null}
        />
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
            Sort
          </span>
          <PillSwitch
            value={sortOrder}
            onChange={(order) => onSortOrderChange(order as 'asc' | 'desc')}>
            <PillSwitchOption value="desc">Newest first</PillSwitchOption>
            <PillSwitchOption value="asc">Oldest first</PillSwitchOption>
          </PillSwitch>
        </div>
      </div>

      <div className="mt-4 border-t border-uniform-gray-200 pt-4 dark:border-uniform-gray-700">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-uniform-gray-700 dark:text-uniform-gray-300">
              Show
            </span>
            <PillSwitch
              size="sm"
              value={matchingPreset(selectedEventTypes) ?? ''}
              onChange={(preset) => onEventTypesChange(new Set(presetEventTypes(preset)))}>
              {TIMELINE_PRESETS.map((preset) => (
                <PillSwitchOption key={preset.value} value={preset.value}>
                  {preset.label}
                </PillSwitchOption>
              ))}
            </PillSwitch>
          </div>
          <Button
            color="secondary"
            disabled={selectedEventTypes.size === 0}
            onClick={() => onEventTypesChange(new Set())}
            size="xs"
            variant="quiet">
            Clear all
          </Button>
        </div>

        <div className="mt-3 grid grid-cols-[max-content_1fr] items-start gap-x-4">
          {TIMELINE_EVENT_GROUPS.map((group) => {
            const groupTypes = eventTypesInGroup(group.value);
            const selectedCount = groupTypes.filter((type) => selectedEventTypes.has(type)).length;

            return (
              <React.Fragment key={group.value}>
                <Checkbox
                  checked={selectedCount === groupTypes.length}
                  className="self-stretch border-r border-uniform-gray-200 py-1 pr-4 dark:border-uniform-gray-700"
                  color="neutral"
                  id={`timeline-group-${group.value}`}
                  indeterminate={selectedCount > 0 && selectedCount < groupTypes.length}
                  label={
                    <span className="text-xs font-semibold tracking-wide text-uniform-gray-500 uppercase dark:text-uniform-gray-400">
                      {group.label}
                    </span>
                  }
                  onChange={(isSelected) => setGroupSelected(groupTypes, isSelected)}
                />
                <div className="flex flex-wrap gap-x-4 gap-y-2 py-1">
                  {TIMELINE_EVENT_TYPES.filter((type) => type.group === group.value).map((type) => (
                    <Checkbox
                      key={type.value}
                      checked={selectedEventTypes.has(type.value)}
                      id={`timeline-type-${type.slug}`}
                      label={type.label}
                      onChange={() => toggleEventType(type.value)}
                    />
                  ))}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex min-h-6 items-center justify-between gap-2 border-t border-uniform-gray-200 pt-3 dark:border-uniform-gray-700">
        <span className="text-sm text-uniform-gray-500 dark:text-uniform-gray-400">
          {eventCounts &&
            `Showing ${eventCounts.shown} of ${eventCounts.total} event${eventCounts.total === 1 ? '' : 's'}`}
        </span>
        {hasActiveFilters && (
          <Button color="secondary" onClick={onResetFilters} size="xs" variant="quiet">
            Reset filters
          </Button>
        )}
      </div>
    </Card>
  );
};

export default TimelineControls;
